// src/contexts/ProfileContext.jsx
// ─────────────────────────────────────────────────────────────────
//  Universal profile context — every user is a disciple who
//  makes disciples. The 2 Tim 2:2 model applies to all roles;
//  only the vocabulary and optional extra features change.
//
//  Exports:
//    ROLES                  — array of role definition objects
//    LIFE_STAGES            — array of life-stage definition objects
//    ROLE_LIFE_STAGES       — map of role → suggested life stages
//    ProfileProvider        — wrap inside AuthProvider
//    useProfile()           — returns profile state + helpers
//
//  FIX #4: profileReady timeout guard.
//    If useSyncStore never resolves `ready` (private browsing, corrupted IDB,
//    storage quota exceeded, Safari ITP), the app was stuck on Splash forever.
//    After 8 s we force effectiveReady = true using the default profile so the
//    user can reach the app. profileTimedOut is exposed so AppContent can show
//    a toast warning about incomplete data.
// ─────────────────────────────────────────────────────────────────
import React, {
  createContext, useContext, useCallback, useRef, useEffect, useState,
} from 'react';
import { useSyncStore } from '@/hooks/sync/useSyncStore';
import { STORE_USER_PROFILE } from '@/lib/storeKeys';

// ── Role definitions ──────────────────────────────────────────
// Every role shares the same discipleship engine (2 Tim 2:2).
// Only labels, context names, and extra features differ.
export const ROLES = [
  // ── Pastoral / Church Leadership ──────────────────────────
  {
    id:            'planter',
    label:         'Church Planter',
    emoji:         '🌱',
    description:   'Planting a new church or ministry from the ground up',
    gen1Label:     'Timothy',
    gen1Plural:    'Timothys',
    gen2Label:     'Faithful Men',
    gen2Plural:    'Faithful Men',
    gen3Label:     'Others',
    gen3Plural:    'Others',
    peopleLabel:   'Contacts',
    contextPrompt: 'What is the name of your church plant?',
    contextDefault:'My Church Plant',
    extraFeatures: ['stewardship', 'reports', 'calendar'],
    budgetContext: 'Ministry Budget',
    journalContext:'Ministry',
  },
  {
    id:            'pastor',
    label:         'Pastor',
    emoji:         '⛪',
    description:   'Leading and shepherding an established congregation',
    gen1Label:     'Timothy',
    gen1Plural:    'Timothys',
    gen2Label:     'Faithful Men',
    gen2Plural:    'Faithful Men',
    gen3Label:     'Others',
    gen3Plural:    'Others',
    peopleLabel:   'Contacts',
    contextPrompt: 'What is the name of your church?',
    contextDefault:'My Church',
    extraFeatures: ['sermonNotes', 'calendar', 'stewardship', 'reports'],
    budgetContext: 'Church Budget',
    journalContext:'Ministry',
  },
  {
    id:            'elder',
    label:         'Elder / Senior Leader',
    emoji:         '🏛️',
    description:   'Mentoring the next generation of leaders with wisdom',
    gen1Label:     'Timothy',
    gen1Plural:    'Timothys',
    gen2Label:     'Faithful Men',
    gen2Plural:    'Faithful Men',
    gen3Label:     'Others',
    gen3Plural:    'Others',
    peopleLabel:   'Contacts',
    contextPrompt: 'What is the name of your church or ministry?',
    contextDefault:'My Ministry',
    extraFeatures: ['sermonNotes', 'calendar'],
    budgetContext: 'Ministry Budget',
    journalContext:'Ministry',
  },
  {
    id:            'deacon',
    label:         'Deacon',
    emoji:         '🤲',
    description:   'Serving the church body and making disciples through service',
    gen1Label:     'Timothy',
    gen1Plural:    'Timothys',
    gen2Label:     'Faithful Men',
    gen2Plural:    'Faithful Men',
    gen3Label:     'Others',
    gen3Plural:    'Others',
    peopleLabel:   'Contacts',
    contextPrompt: 'Which church or ministry do you serve in?',
    contextDefault:'My Church',
    extraFeatures: ['calendar', 'stewardship'],
    budgetContext: 'Ministry Budget',
    journalContext:'Ministry',
  },
  {
    id:            'widowedPastor',
    label:         'Widowed Pastor',
    emoji:         '🕊️',
    description:   'Continuing faithful ministry through grief and perseverance',
    gen1Label:     'Timothy',
    gen1Plural:    'Timothys',
    gen2Label:     'Faithful Men',
    gen2Plural:    'Faithful Men',
    gen3Label:     'Others',
    gen3Plural:    'Others',
    peopleLabel:   'Contacts',
    contextPrompt: 'What is the name of your church or ministry?',
    contextDefault:'My Church',
    extraFeatures: ['sermonNotes', 'calendar', 'stewardship', 'reports'],
    budgetContext: 'Church Budget',
    journalContext:'Ministry',
  },

  // ── Outreach & Evangelism ──────────────────────────────────
  {
    id:            'missionary',
    label:         'Missionary',
    emoji:         '✈️',
    description:   'Reaching unreached peoples and planting churches cross-culturally',
    gen1Label:     'Timothy',
    gen1Plural:    'Timothys',
    gen2Label:     'Faithful Men',
    gen2Plural:    'Faithful Men',
    gen3Label:     'Others',
    gen3Plural:    'Others',
    peopleLabel:   'Contacts',
    contextPrompt: 'Where is your mission field?',
    contextDefault:'My Mission Field',
    extraFeatures: ['stewardship', 'calendar', 'reports'],
    budgetContext: 'Mission Budget',
    journalContext:'Mission',
  },
  {
    id:            'evangelist',
    label:         'Evangelist',
    emoji:         '📢',
    description:   'Sharing the Gospel and leading people to faith',
    gen1Label:     'Timothy',
    gen1Plural:    'Timothys',
    gen2Label:     'Faithful Men',
    gen2Plural:    'Faithful Men',
    gen3Label:     'Others',
    gen3Plural:    'Others',
    peopleLabel:   'Contacts',
    contextPrompt: 'What is your ministry or outreach name?',
    contextDefault:'My Outreach',
    extraFeatures: ['calendar'],
    budgetContext: 'Ministry Budget',
    journalContext:'Outreach',
  },

  // ── Small Group & Cell Ministry ────────────────────────────
  {
    id:            'cellLeader',
    label:         'Cell Group Leader',
    emoji:         '🏠',
    description:   'Leading a small group or cell church in your home or community',
    gen1Label:     'Timothy',
    gen1Plural:    'Timothys',
    gen2Label:     'Faithful Men',
    gen2Plural:    'Faithful Men',
    gen3Label:     'Others',
    gen3Plural:    'Others',
    peopleLabel:   'Contacts',
    contextPrompt: 'What is your cell group name?',
    contextDefault:'My Cell Group',
    extraFeatures: ['calendar'],
    budgetContext: 'Group Budget',
    journalContext:'Cell Ministry',
  },

  // ── Youth & Next Gen ───────────────────────────────────────
  {
    id:            'youthLeader',
    label:         'Youth Leader',
    emoji:         '🎯',
    description:   'Discipling the next generation through youth ministry',
    gen1Label:     'Timothy',
    gen1Plural:    'Timothys',
    gen2Label:     'Faithful Men',
    gen2Plural:    'Faithful Men',
    gen3Label:     'Others',
    gen3Plural:    'Others',
    peopleLabel:   'Contacts',
    contextPrompt: 'What is your youth group name?',
    contextDefault:'My Youth Group',
    extraFeatures: ['calendar'],
    budgetContext: 'Youth Budget',
    journalContext:'Youth Ministry',
  },

  // ── Worship ───────────────────────────────────────────────
  {
    id:            'worshipLeader',
    label:         'Worship Leader',
    emoji:         '🎵',
    description:   'Leading worship and discipling through music and arts ministry',
    gen1Label:     'Timothy',
    gen1Plural:    'Timothys',
    gen2Label:     'Faithful Men',
    gen2Plural:    'Faithful Men',
    gen3Label:     'Others',
    gen3Plural:    'Others',
    peopleLabel:   'Contacts',
    contextPrompt: 'What is your worship team or ministry name?',
    contextDefault:'My Worship Team',
    extraFeatures: ['calendar'],
    budgetContext: 'Ministry Budget',
    journalContext:'Worship Ministry',
  },

  // ── Family ────────────────────────────────────────────────
  {
    id:            'father',
    label:         'Father',
    emoji:         '👨‍👧‍👦',
    description:   'Discipling your children and leading your home for Christ',
    gen1Label:     'Child',
    gen1Plural:    'Children',
    gen2Label:     'Grandchild',
    gen2Plural:    'Grandchildren',
    gen3Label:     'Others',
    gen3Plural:    'Others',
    peopleLabel:   'Family',
    contextPrompt: 'What is your family name?',
    contextDefault:'My Family',
    extraFeatures: [],
    budgetContext: 'Family Budget',
    journalContext:'Family',
  },
  {
    id:            'mother',
    label:         'Mother',
    emoji:         '👩‍👧‍👦',
    description:   'Discipling your children and nurturing your home in faith',
    gen1Label:     'Child',
    gen1Plural:    'Children',
    gen2Label:     'Grandchild',
    gen2Plural:    'Grandchildren',
    gen3Label:     'Others',
    gen3Plural:    'Others',
    peopleLabel:   'Family',
    contextPrompt: 'What is your family name?',
    contextDefault:'My Family',
    extraFeatures: [],
    budgetContext: 'Family Budget',
    journalContext:'Family',
  },

  // ── Marketplace ───────────────────────────────────────────
  {
    id:            'professional',
    label:         'Marketplace Believer',
    emoji:         '💼',
    description:   'Making disciples through your workplace and professional sphere',
    gen1Label:     'Colleague',
    gen1Plural:    'Colleagues',
    gen2Label:     'Their Team',
    gen2Plural:    'Their Teams',
    gen3Label:     'Others',
    gen3Plural:    'Others',
    peopleLabel:   'Contacts',
    contextPrompt: 'What is your workplace or industry?',
    contextDefault:'My Workplace',
    extraFeatures: [],
    budgetContext: 'Budget',
    journalContext:'Workplace',
  },

  // ── Next Gen ──────────────────────────────────────────────
  {
    id:            'student',
    label:         'Student',
    emoji:         '🎓',
    description:   'Living for Christ on campus and making disciples among peers',
    gen1Label:     'Friend',
    gen1Plural:    'Friends',
    gen2Label:     'Their Friends',
    gen2Plural:    'Their Friends',
    gen3Label:     'Others',
    gen3Plural:    'Others',
    peopleLabel:   'Friends',
    contextPrompt: 'What is your school or university?',
    contextDefault:'My Campus',
    extraFeatures: [],
    budgetContext: 'Budget',
    journalContext:'Campus',
  },

  // ── General ───────────────────────────────────────────────
  {
    id:            'single',
    label:         'Single Believer',
    emoji:         '🌟',
    description:   'Living fully for Christ in the gift of singleness',
    gen1Label:     'Friend',
    gen1Plural:    'Friends',
    gen2Label:     'Their Friends',
    gen2Plural:    'Their Friends',
    gen3Label:     'Others',
    gen3Plural:    'Others',
    peopleLabel:   'Friends',
    contextPrompt: 'What is your discipleship context?',
    contextDefault:'My Community',
    extraFeatures: [],
    budgetContext: 'Budget',
    journalContext:'Life',
  },
  {
    id:            'layBeliever',
    label:         'Lay Believer',
    emoji:         '🙏',
    description:   'A faithful disciple making disciples in everyday life',
    gen1Label:     'Friend',
    gen1Plural:    'Friends',
    gen2Label:     'Their Friends',
    gen2Plural:    'Their Friends',
    gen3Label:     'Others',
    gen3Plural:    'Others',
    peopleLabel:   'Friends',
    contextPrompt: 'What is your discipleship context?',
    contextDefault:'My Community',
    extraFeatures: [],
    budgetContext: 'Budget',
    journalContext:'Life',
  },
];

// ── Life stage definitions ─────────────────────────────────────
export const LIFE_STAGES = [
  // ── Youth & Young Adults ──────────────────────────────────
  {
    id:          'youth',
    label:       'Youth / Teen',
    emoji:       '🌱',
    ageRange:    '13–17',
    tagline:     'Your faith is being formed right now',
    description: 'You are in your teenage years — a time of enormous change, identity formation, and foundational decisions. The faith habits you build now will shape the rest of your life. God is not waiting for you to grow up to use you; He is working in and through you right now.',
  },
  {
    id:          'collegeStudent',
    label:       'College / University Student',
    emoji:       '🎓',
    ageRange:    '18–24',
    tagline:     'The most formative years of your life',
    description: 'You are navigating higher education, new independence, and the biggest questions of life. Who am I? What do I believe? What will I do with my life? These years are both spiritually dangerous and spiritually rich. Rooting yourself in community, Scripture, and discipleship now will shape everything that follows.',
  },
  {
    id:          'youngAdult',
    label:       'Young Adult',
    emoji:       '🌤️',
    ageRange:    '22–30',
    tagline:     'Building your life on the right foundation',
    description: 'You are in your early adult years — finding your footing in career, relationships, finances, and faith. This is a season of enormous potential and enormous pressure. The decisions you make now about marriage, calling, money, and discipleship will define the next 30 years.',
  },
  {
    id:          'singleProfessional',
    label:       'Single Professional',
    emoji:       '💼',
    ageRange:    '25–40',
    tagline:     'Fully alive for God in this season',
    description: 'You are single and working — whether by choice, circumstance, or calling. You have a freedom that married people do not have and an availability that is a gift to the Kingdom. But singleness in a couples-focused church can feel invisible. You are fully seen by God and fully called.',
  },
  {
    id:          'singleJobHunting',
    label:       'Single & Job Hunting',
    emoji:       '🔍',
    ageRange:    '',
    tagline:     'God is your provider in the waiting',
    description: 'You are single and looking for work — a season that can feel like limbo. Financial pressure, uncertainty, and comparison to peers can all weigh heavily. But God is working even in the waiting. This season is not wasted; it is preparation.',
  },
  {
    id:          'singleMissionaryOrMinistry',
    label:       'Single in Ministry / Mission',
    emoji:       '✝️',
    ageRange:    '',
    tagline:     'Undivided devotion — 1 Cor 7:34',
    description: 'You have given your life to ministry or missions as a single person. You carry a freedom Paul celebrated in 1 Corinthians 7 — undivided devotion to God. But you also carry unique burdens: loneliness on the field, questions about the future, and the weight of serving without a partner.',
  },
  // ── Marriage & Family ─────────────────────────────────────
  {
    id:          'youngMarried',
    label:       'Newly / Young Married',
    emoji:       '💍',
    ageRange:    '22–35',
    tagline:     'Building your home on Christ together',
    description: 'You are in the early years of marriage — learning to live as one, navigate conflict, build rhythms of faith together, and prepare for whatever God has next. The habits of prayer, Scripture, and discipleship you build together now will carry you through every season ahead.',
  },
  {
    id:          'marriedDualIncome',
    label:       'Married — Dual Income, No Kids',
    emoji:       '👫',
    ageRange:    '',
    tagline:     'Investing this season with eternity in mind',
    description: 'You and your spouse are both working with no children yet. You have more margin — time and money — than most seasons of life will ever give you again. How you steward this window will shape your generosity, ministry involvement, and financial foundation for the rest of your marriage.',
  },
  {
    id:          'parentYoungChildren',
    label:       'Parent of Young Children',
    emoji:       '👶',
    ageRange:    '',
    tagline:     'Discipling the ones God gave you',
    description: 'You have young children at home and life is beautifully, exhaustingly full. Sleep is short, margin is thin, and most of your discipleship right now happens at the dinner table, in the car, and at bedtime. This is not a lesser season — it is one of the most important discipleship assignments God will ever give you.',
  },
  {
    id:          'parentTeenagers',
    label:       'Parent of Teenagers',
    emoji:       '🧑‍🤝‍🧑',
    ageRange:    '',
    tagline:     'The last chapter before they launch',
    description: 'Your children are teenagers — still at home but already leaving. You have a shrinking window to speak into their lives before they step into full independence. This is a season of releasing, trusting, and having the hard conversations while you still can.',
  },
  // ── Midlife ───────────────────────────────────────────────
  {
    id:          'midlife',
    label:       'Midlife',
    emoji:       '🧭',
    ageRange:    '40–55',
    tagline:     'Your most fruitful years are ahead',
    description: 'You are in the middle decades of life with experience, influence, and hard-won wisdom. You may be at the peak of your career or ministry. This is often your most fruitful season but also one that brings the deepest questions about legacy and purpose.',
  },
  {
    id:          'emptyNester',
    label:       'Empty Nester',
    emoji:       '🦅',
    ageRange:    '50+',
    tagline:     'Your new season of freedom for God',
    description: 'Your children have grown and left home. You have more time, fewer responsibilities at home, and a new freedom. This is a season of rediscovery — of your marriage, your calling, and your availability to serve God in new ways.',
  },
  // ── Challenges ────────────────────────────────────────────
  {
    id:          'singleParent',
    label:       'Single Parent',
    emoji:       '🙏',
    ageRange:    '',
    tagline:     'God is your partner and provider',
    description: 'You are raising your children alone — whether through divorce, death, or other circumstances. You carry the full weight of parenting without a partner. God is your co-parent, your provider, and your strength in the hardest season of your life.',
  },
  {
    id:          'divorcedSeparated',
    label:       'Divorced / Separated',
    emoji:       '🫶',
    ageRange:    '',
    tagline:     'Healing and walking forward with God',
    description: 'Your marriage has ended or you are in a painful season of separation. You are walking through grief, adjustment, and the rebuilding of your identity. God meets you in the broken place with healing, no condemnation, and a new beginning.',
  },
  {
    id:          'widowed',
    label:       'Widowed',
    emoji:       '🕊️',
    ageRange:    '',
    tagline:     'Walking through grief with God',
    description: 'You have lost your spouse. This is one of the deepest griefs a person can carry. God is near to the brokenhearted and He is your companion through every season of mourning, remembrance, and slowly rebuilding a life in His grace.',
  },
  // ── Senior ────────────────────────────────────────────────
  {
    id:          'senior',
    label:       'Senior / Retired',
    emoji:       '🌿',
    ageRange:    '60+',
    tagline:     'Still bearing much fruit',
    description: 'You are in the senior season of life, retired or slowing down from your primary career. You carry decades of wisdom, experience, and testimony. Your fruit in this season may look different but is no less significant to God and to those you disciple.',
  },
  {
    id:          'seniorActiveMinistry',
    label:       'Senior in Active Ministry',
    emoji:       '🌳',
    ageRange:    '60+',
    tagline:     'Finishing well — 2 Tim 4:7',
    description: 'You are in your senior years and still actively serving in ministry, preaching, discipling, or leading. You are determined to finish well and give everything you have left to the kingdom. Your legacy is being written in the final chapters of a faithful life.',
  },
];

// ── Role → valid life stage IDs ───────────────────────────────
// Each role gets a curated list; still ordered naturally.
export const ROLE_LIFE_STAGES = {
  planter:                ['youngAdult','singleProfessional','youngMarried','marriedDualIncome','parentYoungChildren','parentTeenagers','midlife','emptyNester','senior','seniorActiveMinistry','widowed'],
  pastor:                 ['youngAdult','youngMarried','parentYoungChildren','parentTeenagers','midlife','emptyNester','senior','seniorActiveMinistry','widowed'],
  elder:                  ['midlife','emptyNester','senior','seniorActiveMinistry','widowed'],
  deacon:                 ['youngAdult','youngMarried','parentYoungChildren','midlife','emptyNester','senior'],
  widowedPastor:          ['youngAdult','parentYoungChildren','midlife','emptyNester','senior','seniorActiveMinistry'],
  missionary:             ['youngAdult','singleProfessional','singleMissionaryOrMinistry','youngMarried','marriedDualIncome','parentYoungChildren','midlife','senior'],
  evangelist:             ['youngAdult','singleProfessional','singleMissionaryOrMinistry','youngMarried','parentYoungChildren','midlife','senior'],
  cellLeader:             ['youngAdult','singleProfessional','youngMarried','parentYoungChildren','midlife','senior'],
  youthLeader:            ['youngAdult','singleProfessional','youngMarried','parentYoungChildren','midlife'],
  worshipLeader:          ['youngAdult','singleProfessional','singleMissionaryOrMinistry','youngMarried','parentYoungChildren','midlife','senior'],
  father:                 ['youngMarried','parentYoungChildren','parentTeenagers','midlife','emptyNester','senior','widowed'],
  mother:                 ['youngMarried','parentYoungChildren','parentTeenagers','midlife','emptyNester','senior','widowed'],
  professional:           ['youngAdult','singleProfessional','youngMarried','marriedDualIncome','parentYoungChildren','midlife','emptyNester','senior'],
  student:                ['youth','collegeStudent','youngAdult'],
  single:                 ['youth','youngAdult','singleProfessional','singleJobHunting','singleMissionaryOrMinistry','midlife','senior','divorcedSeparated','widowed'],
  layBeliever:            ['youth','youngAdult','singleProfessional','singleJobHunting','youngMarried','marriedDualIncome','parentYoungChildren','parentTeenagers','midlife','emptyNester','singleParent','divorcedSeparated','senior','widowed'],
};

// ── Default profile shape ──────────────────────────────────────
const DEFAULT_PROFILE = {
  role:             'planter',
  lifeStage:        'youngAdult',
  displayName:      '',
  ministryName:     '',
  location:         '',
  completedSetup:   false,
  setupCompletedAt: null,
};

// ── IDB ready timeout — 8 seconds ─────────────────────────────
const PROFILE_READY_TIMEOUT_MS = 8000;

// ── Context ───────────────────────────────────────────────────
const ProfileContext = createContext(null);

export function ProfileProvider({ children }) {
  const [profile, setProfileRaw, ready] = useSyncStore(STORE_USER_PROFILE, DEFAULT_PROFILE);

  // FIX #4: If useSyncStore never resolves `ready` (private browsing,
  // corrupted IDB, Safari ITP, quota exceeded) the app was stuck on the
  // Splash screen with no recovery path. After PROFILE_READY_TIMEOUT_MS we
  // force effectiveReady = true so the user can reach the app with a
  // default profile, and we surface profileTimedOut so AppContent can show
  // a warning toast.
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (ready) return; // resolved normally — no timeout needed
    const timer = setTimeout(() => {
      console.warn('[ProfileContext] useSyncStore did not resolve in 8 s — forcing ready with default profile');
      setTimedOut(true);
    }, PROFILE_READY_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [ready]);

  const effectiveReady = ready || timedOut;

  // Always return a fully-shaped profile (migration safety)
  const safeProfile = { ...DEFAULT_PROFILE, ...(profile || {}) };

  // ── Auto-detect existing accounts ────────────────────────────
  // An account is considered "set up" if:
  //   1. completedSetup flag is explicitly true, OR
  //   2. The synced profile has real data (displayName, non-default role,
  //      or ministryName) — this covers existing users signing in on a new
  //      device where completedSetup was never written, or was lost.
  const hasRealProfileData = !!(
    safeProfile.displayName?.trim() ||
    safeProfile.ministryName?.trim() ||
    safeProfile.location?.trim() ||
    (safeProfile.role && safeProfile.role !== DEFAULT_PROFILE.role) ||
    (safeProfile.lifeStage && safeProfile.lifeStage !== DEFAULT_PROFILE.lifeStage) ||
    safeProfile.setupCompletedAt
  );

  // If synced data looks like a real profile but completedSetup is false,
  // silently fix it so the user is never shown the wizard again.
  // FIX #4: use effectiveReady instead of ready so this also works after timeout.
  const hasCompletedSetup = safeProfile.completedSetup === true || (effectiveReady && hasRealProfileData);

  // Auto-heal: write the flag back once so future loads are instant
  const autoHealedRef = useRef(false);
  useEffect(() => {
    if (ready && hasRealProfileData && !safeProfile.completedSetup && !autoHealedRef.current) {
      autoHealedRef.current = true;
      setProfileRaw(prev => ({
        ...DEFAULT_PROFILE,
        ...(prev || {}),
        completedSetup:   true,
        setupCompletedAt: prev?.setupCompletedAt ?? new Date().toISOString(),
      }));
    }
    // Note: we intentionally only auto-heal when `ready` (real IDB data),
    // not when timedOut, to avoid writing a stale default profile back to IDB.
  }, [ready, hasRealProfileData, safeProfile.completedSetup, setProfileRaw]);

  const updateProfile = useCallback((updates) => {
    setProfileRaw(prev => ({
      ...DEFAULT_PROFILE,
      ...(prev || {}),
      ...updates,
    }));
  }, [setProfileRaw]);

  const completeSetup = useCallback((profileData) => {
    setProfileRaw({
      ...DEFAULT_PROFILE,
      ...profileData,
      completedSetup:   true,
      setupCompletedAt: new Date().toISOString(),
    });
  }, [setProfileRaw]);

  const getRoleData = useCallback(() => (
    ROLES.find(r => r.id === safeProfile.role) ?? ROLES[0]
  ), [safeProfile.role]);

  const getLifeStageData = useCallback(() => (
    LIFE_STAGES.find(s => s.id === safeProfile.lifeStage) ?? LIFE_STAGES[2]
  ), [safeProfile.lifeStage]);

  const resetSetup = useCallback(() => {
    setProfileRaw({ ...DEFAULT_PROFILE });
  }, [setProfileRaw]);

  const value = {
    profile:           safeProfile,
    // FIX #4: profileReady uses effectiveReady so Splash unblocks after timeout
    profileReady:      effectiveReady,
    // FIX #4: expose timedOut flag so AppContent can show a warning toast
    profileTimedOut:   timedOut,
    hasCompletedSetup,
    updateProfile,
    completeSetup,
    resetSetup,
    getRoleData,
    getLifeStageData,
    role:      safeProfile.role,
    lifeStage: safeProfile.lifeStage,
  };

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile() {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error('useProfile must be used inside <ProfileProvider>');
  return ctx;
}